import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, uploadFile } from "@/lib/api";

const Field = ({ label, children }) => <div className="space-y-2"><Label>{label}</Label>{children}</div>;
const FIELDS = [["full_name", "Full name"], ["headline", "Headline"], ["designation", "Designation"], ["firm_name", "Firm name"], ["city", "City"], ["website", "Website"], ["whatsapp", "WhatsApp number"], ["contact_number", "Contact number"]];

export default function EditProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    ...Object.fromEntries(FIELDS.map(([k]) => [k, user[k] || ""])), bio: user.bio || "",
    specializations: (user.specializations || []).join(", "), languages: (user.languages || []).join(", "),
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { ...form, specializations: list(form.specializations), languages: list(form.languages) });
      setUser(data?.id ? data : { ...user, ...form, specializations: list(form.specializations), languages: list(form.languages) });
      toast.success("Profile updated");
      navigate(`/profile/${user.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    try {
      const avatar = await uploadFile(file);
      const { data } = await api.put("/auth/profile", { avatar });
      setUser(data?.id ? data : { ...user, avatar });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <form onSubmit={save} className="mx-auto max-w-3xl space-y-6" data-testid="edit-profile-form">
      <div><p className="tm-eyebrow">Settings</p><h1 className="mt-2 font-display text-2xl font-extrabold text-tm-green sm:text-3xl">Edit your profile</h1>
        <p className="mt-1 text-sm text-slate-500">Changes also appear in the Taxmandi mobile app.</p></div>
      <div className="tm-card flex items-center gap-5 p-6">
        <UserAvatar user={user} size="lg" />
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="avatar-file-input" />
        <Button type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current.click()} data-testid="avatar-upload-button"><Camera className="mr-2 h-4 w-4" />Change photo</Button>
      </div>
      <div className="tm-card grid gap-5 p-6 sm:grid-cols-2 sm:p-8">
        {FIELDS.map(([k, l]) => <Field key={k} label={l}><Input value={form[k]} onChange={set(k)} data-testid={`edit-${k}-input`} /></Field>)}
        <Field label="Specializations (comma separated)"><Input value={form.specializations} onChange={set("specializations")} data-testid="edit-specializations-input" /></Field>
        <Field label="Languages (comma separated)"><Input value={form.languages} onChange={set("languages")} data-testid="edit-languages-input" /></Field>
        <div className="sm:col-span-2"><Field label="About"><Textarea rows={5} value={form.bio} onChange={set("bio")} data-testid="edit-bio-input" /></Field></div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} className="rounded-full bg-tm-green px-8 hover:bg-tm-green/90" data-testid="edit-profile-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes</Button>
      </div>
    </form>
  );
}
